import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IAddPatientRequest,
  IAddStaffRequest,
  IBulkImportStatus,
  ICreateCustomFieldRequest,
  ICustomFieldDefinition,
  IHospitalDetail,
  ISetWardCustomFieldsRequest,
  IUpdateCustomFieldRequest,
  IUpdateHospitalRequest,
} from "../../../@types/admin/AdminHospitalDetails";
import { ISetWardStandardFieldsRequest } from "../../../@types/StandardField";
import { hospitalDetailsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import {
  BULK_IMPORTS_QUERY_KEY,
  CUSTOM_FIELDS_QUERY_KEY,
  HOSPITAL_DETAIL_QUERY_KEY,
  HOSPITAL_PATIENTS_QUERY_KEY,
  HOSPITAL_STAFF_QUERY_KEY,
  HOSPITAL_WARDS_QUERY_KEY,
  WARD_CUSTOM_FIELDS_QUERY_KEY,
  WARD_STANDARD_FIELDS_QUERY_KEY,
} from "../../queries/admin/hospitalDetails";

export function useUpdateHospital() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IHospitalDetail,
    Error,
    { hospitalId: number; request: IUpdateHospitalRequest }
  >({
    mutationFn: async ({ hospitalId, request }) =>
      ensureOk(
        await hospitalDetailsService.updateHospital(hospitalId, request)
      ),
    onSuccess: (_data, { hospitalId }) => {
      queryClient.invalidateQueries({
        queryKey: [HOSPITAL_DETAIL_QUERY_KEY, hospitalId],
      });
      // The Hospitals list shows the name, code and published state.
      queryClient.invalidateQueries({ queryKey: ["admin-hospitals"] });
    },
  });

  return { updateHospital: mutateAsync, isUpdatingHospital: isPending };
}

/**
 * Adds a patient to one of the hospital's wards.
 *
 * ⚠️ Creates a patient record and, depending on the ward's configuration, an
 * account the patient can sign into. Always behind a confirmation.
 */
export function useAddHospitalPatient() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { request: IAddPatientRequest }
  >({
    mutationFn: async ({ request }) =>
      ensureOk(await hospitalDetailsService.addPatient(request)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [HOSPITAL_PATIENTS_QUERY_KEY],
      });
      // The patient also appears in the global patient list.
      queryClient.invalidateQueries({ queryKey: ["admin-patients"] });
    },
  });

  return { addPatient: mutateAsync, isAddingPatient: isPending };
}

/**
 * Adds a staff member to the hospital.
 *
 * ⚠️ Creates an account that can read patient data; the permission level
 * decides how much.
 */
export function useAddHospitalStaff() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { request: IAddStaffRequest }
  >({
    mutationFn: async ({ request }) =>
      ensureOk(await hospitalDetailsService.addStaff(request)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HOSPITAL_STAFF_QUERY_KEY] });
      // The staff member also appears in the global staff list.
      queryClient.invalidateQueries({ queryKey: ["admin-staff"] });
    },
  });

  return { addStaff: mutateAsync, isAddingStaff: isPending };
}

/**
 * Tells one patient's app to re-read its workflow configuration.
 *
 * A silent push: the patient sees nothing. Nothing to invalidate — the response
 * changes no data this page shows.
 */
export function useRefreshWorkflowConfig() {
  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { userId: number }
  >({
    mutationFn: async ({ userId }) =>
      ensureOk(await hospitalDetailsService.refreshWorkflowConfig(userId)),
  });

  return { refreshWorkflowConfig: mutateAsync, isRefreshing: isPending };
}

function invalidateCustomFields(
  queryClient: ReturnType<typeof useQueryClient>,
  hospitalGroupId: number
) {
  queryClient.invalidateQueries({
    queryKey: [CUSTOM_FIELDS_QUERY_KEY, hospitalGroupId],
  });
  // The templates response flags which presets are already added.
  queryClient.invalidateQueries({
    queryKey: ["admin-custom-field-templates", hospitalGroupId],
  });
  // Every ward of the hospital lists the definitions, so their configuration
  // views are stale too.
  queryClient.invalidateQueries({ queryKey: [WARD_CUSTOM_FIELDS_QUERY_KEY] });
}

export function useCreateCustomField() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ICustomFieldDefinition[],
    Error,
    { request: ICreateCustomFieldRequest }
  >({
    mutationFn: async ({ request }) =>
      ensureOk(await hospitalDetailsService.createCustomField(request)),
    onSuccess: (_data, { request }) =>
      invalidateCustomFields(queryClient, request.hospitalGroupId),
  });

  return { createCustomField: mutateAsync, isCreatingCustomField: isPending };
}

export function useUpdateCustomField() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ICustomFieldDefinition,
    Error,
    { id: string; hospitalGroupId: number; request: IUpdateCustomFieldRequest }
  >({
    mutationFn: async ({ id, request }) =>
      ensureOk(await hospitalDetailsService.updateCustomField(id, request)),
    onSuccess: (_data, { hospitalGroupId }) =>
      invalidateCustomFields(queryClient, hospitalGroupId),
  });

  return { updateCustomField: mutateAsync, isUpdatingCustomField: isPending };
}

/**
 * Deactivates a definition. DELETE, but the answers recorded against it stay
 * and it can be reactivated with `useUpdateCustomField({ isActive: true })`.
 */
export function useDeactivateCustomField() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { id: string; hospitalGroupId: number }
  >({
    mutationFn: async ({ id }) =>
      ensureOk(await hospitalDetailsService.deactivateCustomField(id)),
    onSuccess: (_data, { hospitalGroupId }) =>
      invalidateCustomFields(queryClient, hospitalGroupId),
  });

  return {
    deactivateCustomField: mutateAsync,
    isDeactivatingCustomField: isPending,
  };
}

export function useCreateCustomFieldsFromTemplates() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ICustomFieldDefinition[],
    Error,
    { hospitalGroupId: number; templateKeys: string[] }
  >({
    mutationFn: async ({ hospitalGroupId, templateKeys }) =>
      ensureOk(
        await hospitalDetailsService.createCustomFieldsFromTemplates(
          hospitalGroupId,
          templateKeys
        )
      ),
    onSuccess: (_data, { hospitalGroupId }) =>
      invalidateCustomFields(queryClient, hospitalGroupId),
  });

  return { addFromTemplates: mutateAsync, isAddingFromTemplates: isPending };
}

/**
 * Replaces a ward's custom-field configuration.
 *
 * ⚠️ The whole set, not a delta — a field omitted from the payload loses its
 * configuration. The form always sends every definition.
 */
export function useSetWardCustomFields() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { wardId: number; request: ISetWardCustomFieldsRequest }
  >({
    mutationFn: async ({ wardId, request }) =>
      ensureOk(
        await hospitalDetailsService.setWardCustomFields(wardId, request)
      ),
    onSuccess: (_data, { wardId }) => {
      queryClient.invalidateQueries({
        queryKey: [WARD_CUSTOM_FIELDS_QUERY_KEY, wardId],
      });
      // A field newly shown in the add-patient form changes that drawer.
      queryClient.invalidateQueries({ queryKey: [HOSPITAL_WARDS_QUERY_KEY] });
    },
  });

  return {
    setWardCustomFields: mutateAsync,
    isSavingWardCustomFields: isPending,
  };
}

/**
 * EN-5192: replace a ward's built-in onboarding-field settings. Like the custom-field PUT this
 * is a full replace, so the modal always sends every code.
 */
export function useSetWardStandardFields() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { wardId: number; request: ISetWardStandardFieldsRequest }
  >({
    mutationFn: async ({ wardId, request }) =>
      ensureOk(
        await hospitalDetailsService.setWardStandardFields(wardId, request)
      ),
    onSuccess: (_data, { wardId }) => {
      queryClient.invalidateQueries({
        queryKey: [WARD_STANDARD_FIELDS_QUERY_KEY, wardId],
      });
      // The add-patient form reads the same settings through its own portal endpoint.
      queryClient.invalidateQueries({
        queryKey: ["standard-fields-for-wards"],
      });
    },
  });

  return {
    setWardStandardFields: mutateAsync,
    isSavingWardStandardFields: isPending,
  };
}

/**
 * Queues a CSV bulk import into one ward.
 *
 * ⚠️ Creates patient records in bulk. A wrong file or a wrong ward creates many
 * bad records, so this is always behind a confirmation that names the ward.
 */
export function useStartBulkImport() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IBulkImportStatus,
    Error,
    { file: File; wardId: number }
  >({
    mutationFn: async ({ file, wardId }) =>
      ensureOk(await hospitalDetailsService.startBulkImport(file, wardId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BULK_IMPORTS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HOSPITAL_PATIENTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [HOSPITAL_STAFF_QUERY_KEY] });
    },
  });

  return { startBulkImport: mutateAsync, isStartingBulkImport: isPending };
}
